import { useMemo, useState } from "react"
import { isBlurhashValid } from "blurhash"
import { Blurhash } from "react-blurhash"

import { cn } from "@productlane/lib/src/browser"

import { readCachedImage } from "@/lib/cached-image"
import { COVER_HEIGHT, COVER_WIDTH } from "@/lib/process-cover-image"

export type CoverRow =
	| { imageUrl?: string | null; imageBlurhash?: string | null }
	| null
	| undefined

/** The stored hash for this exact file, so a new upload never shows the old blur. */
export function coverBlurhashFor(
	imageUrl: string | null | undefined,
	rows: CoverRow[],
): string | null {
	if (!imageUrl) return null
	return rows.find((row) => row?.imageUrl === imageUrl)?.imageBlurhash ?? null
}

// Holds the standard cover's box until the file loads; the cover's own ratio takes over after.
export function ChangelogCover({
	src,
	blurhash,
}: {
	src: string
	blurhash?: string | null
}) {
	const [loadedSrc, setLoadedSrc] = useState<string | null>(null)
	// A file the browser already holds renders visible on the first frame, so
	// switching back to a changelog never replays the blur.
	const isCached = useMemo(() => readCachedImage(src) !== null, [src])
	const isLoaded = isCached || loadedSrc === src
	const hasBlur = !!blurhash && isBlurhashValid(blurhash).result

	return (
		<div
			className={cn(
				"relative overflow-hidden rounded-lg ring-1 ring-black/20 dark:ring-white/20",
				!isLoaded && !hasBlur && "bg-gray-100 dark:bg-gray-800",
			)}
		>
			<img
				alt="changelog cover"
				src={src}
				width={COVER_WIDTH}
				height={COVER_HEIGHT}
				onLoad={() => setLoadedSrc(src)}
				className={cn(
					"block h-auto w-full transition-opacity duration-200",
					isLoaded || hasBlur ? "opacity-100" : "opacity-0",
				)}
			/>
			{blurhash && (
				<Blurhash
					hash={blurhash}
					width="100%"
					height="100%"
					resolutionX={32}
					resolutionY={32}
					className={cn(
						"!absolute inset-0 overflow-hidden transition-opacity duration-200",
						hasBlur && !isLoaded
							? "opacity-100"
							: "pointer-events-none opacity-0",
					)}
				/>
			)}
		</div>
	)
}

// Same frame as the image cover; the still holds the box until the video plays.
export function ChangelogCoverVideo({
	src,
	poster,
}: {
	src: string
	poster?: string | null
}) {
	return (
		<div className="relative overflow-hidden rounded-lg bg-gray-100 ring-1 ring-black/20 dark:bg-gray-800 dark:ring-white/20">
			<video
				src={src}
				poster={poster ?? undefined}
				width={COVER_WIDTH}
				height={COVER_HEIGHT}
				controls
				muted
				playsInline
				preload="metadata"
				className="block h-auto w-full"
			/>
		</div>
	)
}
